import { useMemo } from "react";
import Box from "@mui/material/Box";

import { useTheme } from "@mui/material/styles";
import { EChartsRenderer } from "@/shared/charts/components/echarts-renderer";
import { createLineAreaChartOption } from "@/shared/charts/components/line-area-chart/option";
import {
  createEChartsTheme,
  type EChartsThemeOverride,
} from "@/shared/charts/theme/create-echarts-theme";
import type { ECharts } from "echarts";
import type {
  LineAreaChartConfig,
  LineAreaChartPoint,
} from "@/shared/charts/components/line-area-chart/types";

interface LineAreaChartProps {
  data: LineAreaChartPoint[];
  config: LineAreaChartConfig;
  height?: number | string;
  testId?: string;
  /** Partial visual-token overrides merged with the MUI-derived chart theme. */
  theme?: EChartsThemeOverride;
  onChartReady?: (instance: ECharts) => void;
}

export function LineAreaChart({
  data,
  config,
  height = 240,
  testId,
  theme,
  onChartReady,
}: LineAreaChartProps) {
  const muiTheme = useTheme();
  const chartTokens = useMemo(() => createEChartsTheme(muiTheme, theme), [muiTheme, theme]);
  const option = useMemo(
    () => createLineAreaChartOption(data, config, chartTokens),
    [data, config, chartTokens]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1, minHeight: 0 }}>
      <EChartsRenderer
        option={option}
        ariaLabel={config.ariaDescription}
        height={height}
        testId={testId}
        onChartReady={onChartReady}
      />
    </Box>
  );
}
